import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/utils/cn';

import { Icon } from './Icon';

export type EditableFieldType = 'text' | 'email' | 'tel' | 'number' | 'date' | 'textarea' | 'select' | 'url';

export interface EditableFieldProps {
  label: ReactNode;
  /** Display string for current value (rendered when not editing). */
  display: ReactNode;
  /** Raw value for the editor input. */
  value?: string;
  type?: EditableFieldType;
  options?: { value: string; label: string }[];
  required?: boolean;
  /** Marks the field as a custom (user-defined) one — adds the "Custom" tag. */
  custom?: boolean;
  /** Disable editing entirely. */
  readOnly?: boolean;
  /** Empty-state placeholder when not editing and value is missing. */
  emptyLabel?: string;
  /** Datalist suggestions for text-like inputs; free text stays allowed. */
  suggestions?: string[];
  /**
   * Render the editor as a filter-as-you-type combobox over `suggestions`
   * (free text still allowed). Use when a counter table is connected to the
   * field; falls back to a plain datalist input when false.
   */
  searchable?: boolean;
  saving?: boolean;
  onSave: (next: string) => void | Promise<void>;
}

export function EditableField({
  label,
  display,
  value,
  type = 'text',
  options,
  required,
  custom,
  readOnly,
  emptyLabel = 'Empty',
  suggestions,
  searchable,
  saving,
  onSave,
}: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? '');
  const [comboOpen, setComboOpen] = useState(false);
  const datalistId = useId();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
  const comboAnchorRef = useRef<HTMLDivElement>(null);

  // Re-sync the draft when the external value changes — adjusted during render
  // (the documented alternative to a setState-in-effect).
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setDraft(value ?? '');
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current) inputRef.current.select?.();
    }
  }, [editing]);

  const commit = async (next?: string) => {
    const nextValue = next ?? draft;
    if (next !== undefined) setDraft(next);
    await onSave(nextValue);
    setComboOpen(false);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value ?? '');
    setComboOpen(false);
    setEditing(false);
  };

  const useCombo = searchable && !!suggestions?.length;
  const comboMatches = useCombo
    ? (suggestions ?? [])
        .filter((s) => s.toLowerCase().includes(draft.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  const isEmpty = (value ?? '') === '';

  return (
    <>
      <div className="field-label">
        <span>
          {label}
          {required && <span className="req">*</span>}
        </span>
        {custom && <span className="field-tag">Custom</span>}
      </div>
      <div
        className={cn(
          'field-value',
          editing && 'editing',
          !editing && isEmpty && 'empty',
          readOnly && 'readonly',
          saving && 'saving',
        )}
        onClick={() => {
          if (!editing && !readOnly) setEditing(true);
        }}
      >
        {!editing ? (
          <>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isEmpty ? emptyLabel : display}
            </span>
            {!readOnly && <Icon name={saving ? 'spinner' : 'edit'} size={13} className="field-edit-icon" />}
          </>
        ) : (
          <div className="field-edit-row">
            {type === 'textarea' ? (
              <textarea
                ref={(el) => {
                  inputRef.current = el;
                }}
                className="field-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') cancel();
                }}
              />
            ) : type === 'select' ? (
              <select
                ref={(el) => {
                  inputRef.current = el;
                }}
                className="field-select"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') cancel();
                }}
              >
                <option value="">—</option>
                {(options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : useCombo ? (
              <div style={{ position: 'relative', flex: 1 }} ref={comboAnchorRef}>
                <input
                  ref={(el) => {
                    inputRef.current = el;
                  }}
                  className="field-input"
                  type={type}
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setComboOpen(true);
                  }}
                  onFocus={() => setComboOpen(true)}
                  // Defer so an option's onMouseDown can fire before close.
                  onBlur={() => window.setTimeout(() => setComboOpen(false), 120)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void commit();
                    if (e.key === 'Escape') cancel();
                  }}
                />
                {comboOpen && comboMatches.length > 0 && (
                  <ComboDropdown anchorRef={comboAnchorRef}>
                    {comboMatches.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="ds-menu-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          void commit(s);
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </ComboDropdown>
                )}
              </div>
            ) : (
              <>
                <input
                  ref={(el) => {
                    inputRef.current = el;
                  }}
                  className="field-input"
                  type={type}
                  value={draft}
                  list={suggestions?.length ? datalistId : undefined}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void commit();
                    if (e.key === 'Escape') cancel();
                  }}
                />
                {!!suggestions?.length && (
                  <datalist id={datalistId}>
                    {suggestions.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                )}
              </>
            )}
            <div className="field-edit-actions">
              <button
                type="button"
                className="field-mini-btn"
                onClick={cancel}
                aria-label="Cancel edit"
              >
                <Icon name="x" size={13} />
              </button>
              <button
                type="button"
                className="field-mini-btn confirm"
                onClick={() => void commit()}
                aria-label="Save"
              >
                <Icon name={saving ? 'spinner' : 'check'} size={13} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Suggestions dropdown for the searchable combo, portalled to document.body so
 * it escapes the section card's `overflow: hidden` (which otherwise clips it
 * when the field sits at the bottom of a panel). Positioned as a fixed overlay
 * tracking the anchor's rect, mirroring the Popover component.
 */
function ComboDropdown({
  anchorRef,
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<CSSProperties>({ visibility: 'hidden' });

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const compute = () => {
      const r = anchor.getBoundingClientRect();
      setPos({ position: 'fixed', top: r.bottom + 4, left: r.left, width: r.width });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [anchorRef]);

  return createPortal(
    <div
      className="ds-popover"
      style={{ ...pos, padding: 4, maxHeight: 260, overflowY: 'auto' }}
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * Wrapper grid for a stack of editable rows. Use one per logical field group
 * inside a SectionCard with `dense`.
 */
export function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="field-grid">{children}</div>;
}
